/**
 * Single error utils for ednov-web: catalog lookup, interpolation,
 * backend mapping, Firebase mapping, and {@link getErrorMessage}.
 * Copy lives in src/constants/error-catalog.ts.
 */

import axios from 'axios';
import {
  BackendErrors,
  CatalogErrors,
  GenericErrors,
  InternalErrors,
  HttpStatusErrors,
  type CatalogErrorCode,
} from '@/constants/errors-catalog';
import { getRoleLabel } from '@/utils/role-label-utils';

export type { CatalogErrorCode };

/** Client-owned catalog error with `{param}` interpolation payload. */
export type InterpolatedInternalError =
  | { code: 'invalidFileType'; fileKind: string }
  | { code: 'tutorUnavailableAtSlot'; names: string }
  | { code: 'tutorsUnavailableAtSlot'; names: string }
  | { code: 'studentUnavailableAtSlot'; names: string }
  | { code: 'studentsUnavailableAtSlot'; names: string }
  | { code: 'alreadyBusyAtSlot'; name: string }
  | { code: 'bulkCreateStillRunning'; createdSessions: number }
  | { code: 'studentsTrimmedAfterTypeChange'; removedCount: number; newMax: number }
  | { code: 'fileTooLarge'; maxMb: number }
  | { code: 'fileFormatRejected'; hint: string }
  | { code: 'passwordRulesFailed'; rules: string }
  | { code: 'bulkCreatePartialFailure'; created: number; failed: number; detail: string }
  | { code: 'tutorSlotConflictMany'; count: number }
  | { code: 'studentSlotConflictMany'; count: number }
  | { code: 'minLength'; minLength: number }
  | { code: 'maxLength'; maxLength: number }
  | { code: 'phoneMinDigits'; minLength: number }
  | { code: 'phoneMaxDigits'; maxLength: number }
  | { code: 'minValue'; min: number }
  | { code: 'maxValue'; max: number }
  | { code: 'customEngagementMinMonths'; minMonths: number }
  | { code: 'tutorMinAge'; minAge: number }
  | { code: 'studentImportInvalidRows'; invalidCount: number }
  | { code: 'eventParticipantAlreadyAdded'; name: string }
  | { code: 'pageOutOfRange'; min: number; max: number }
  | { code: 'maxTutorsCapacityReached'; max: number }
  | { code: 'maxStudentsCapacityReached'; max: number }
  | { code: 'removeStudentsOverCapacity'; count: number; max: number }
  | { code: 'removeTutorsOverCapacity'; count: number; max: number }
  | { code: 'establishmentClassNameRequired'; index: number }
  | { code: 'establishmentClassLevelRequired'; index: number; name: string }
  | { code: 'establishmentClassSchoolYearRequired'; index: number; name: string }
  | { code: 'establishmentClassDuplicate'; name: string; lineA: number; lineB: number }
  | { code: 'slotConflictRange'; start: string; end: string }
  | { code: 'slotConflictFrom'; start: string }
  | { code: 'schoolYearCodeTaken'; yearCode: string }
  | { code: 'schoolYearDatesOverlap'; label: string; start: string; end: string }
  | { code: 'studentCapacityReachedWithMax'; maxStudents: number }
  | { code: 'participantAlreadyNamedTutor'; name: string }
  | { code: 'participantAlreadyNamedStudent'; name: string }
  | { code: 'autoAssignPartialUnassigned'; unassigned: number }
  | { code: 'sessionTerminalNoEditWithStatus'; status: string }
  | { code: 'checkingAvailabilityOf'; name: string }
  | { code: 'autoAssignUnassignedCount'; count: number }
  | { code: 'reportIncompleteTutorsMany'; count: number }
  | { code: 'otherReportsAlsoValidatedMany'; otherCount: number }
  | { code: 'bulkIncompleteReportsMany'; count: number }
  | { code: 'archiveCascadeChildren'; count: number }
  | { code: 'tutorActivePairsBlockArchive'; tutorName: string }
  | { code: 'tutorActivePairsBlockCancelCriminalRecord'; tutorName: string }
  | { code: 'studentActivePairsBlockArchive'; studentName: string };

/** Backend Nest message → catalog copy. */
type Pattern = {
  pattern: RegExp;
  translate: string | ((match: RegExpMatchArray) => string);
};

/** Internal catalog codes whose templates contain `{param}` placeholders. */
const INTERPOLATED_CODES: ReadonlySet<InterpolatedInternalError['code']> = new Set(
  (Object.keys(InternalErrors) as Array<keyof typeof InternalErrors>).filter((key) =>
    /\{[a-zA-Z]\w*\}/.test(InternalErrors[key]),
  ) as InterpolatedInternalError['code'][],
);

/** Replace `{param}` placeholders in an {@link InternalErrors} template. */
function interpolateInternalTemplate(template: string, params: Record<string, unknown>): string {
  return template
    .replace(/\{(\w+)\}/g, (_, key: string) => {
      const value = params[key];
      if (key === 'code' || value == null) return '';
      if (typeof value === 'string') return value;
      if (typeof value === 'number' || typeof value === 'boolean') return `${value}`;
      return '';
    })
    .trim();
}

/** True when `value` is a known catalog error code. */
function isCatalogErrorCode(value: unknown): value is CatalogErrorCode {
  return typeof value === 'string' && Object.hasOwn(CatalogErrors, value);
}

/** True when `value` is an interpolated internal-error payload. */
function isInterpolatedInternalError(value: unknown): value is InterpolatedInternalError {
  if (!value || typeof value !== 'object' || !('code' in value)) return false;
  return INTERPOLATED_CODES.has((value as { code: InterpolatedInternalError['code'] }).code);
}

/** Resolve an interpolated internal error to catalog copy. */
function resolveInterpolatedInternalError(input: InterpolatedInternalError): string {
  const template = InternalErrors[input.code];
  return interpolateInternalTemplate(template, input as unknown as Record<string, unknown>);
}

// ── Backend mapping ──────────────────────────────────────────────────────────

/** Fallback user-facing copy when no catalog match is found. */
const GENERIC = GenericErrors.generic;

/** Class-validator field-name capture used in validation regexes. */
const FIELD_NAME = String.raw`[\w.]+`;

/** French field labels used when translating class-validator messages. */
const FIELD_LABELS: Record<string, string> = {
  email: "L'e-mail",
  phone: 'Le téléphone',
  firstName: 'Le prénom',
  lastName: 'Le nom',
  startDate: 'La date de début',
  endDate: 'La date de fin',
  startTime: 'L’heure de début',
  endTime: 'L’heure de fin',
  exitDate: 'La date de sortie',
  latitude: 'La latitude',
  longitude: 'La longitude',
  password: 'Le mot de passe',
  name: 'Le nom',
  title: 'Le titre',
  description: 'La description',
  date: 'La date',
  status: 'Le statut',
  code: 'Le code',
  label: 'Le libellé',
  content: 'Le contenu',
  file: 'Le fichier',
};

/** French label for a validation field path, or a generic fallback. */
function describeField(field: string): string {
  const last = field.split('.').pop() ?? field;
  return FIELD_LABELS[last] ?? 'cette information';
}

/** Build a French “field must …” sentence, capitalizing the generic fallback. */
function fieldMust(label: string, rest: string): string {
  if (label === 'cette information') return `Cette information ${rest}`;
  return `${label} ${rest}`;
}

/** Class-validator pattern that maps an English `field must …` suffix to French. */
function fieldPattern(
  suffix: string,
  toFrench: (label: string, match: RegExpMatchArray) => string,
): Pattern {
  return {
    pattern: new RegExp(`^(?:each value in )?(${FIELD_NAME}) ${suffix}$`),
    translate: (match) => toFrench(describeField(match[1]), match),
  };
}

/** Class-validator English messages mapped to French field copy. */
const VALIDATION_PATTERNS: readonly Pattern[] = [
  fieldPattern('must be a string', (label) => fieldMust(label, 'doit être du texte')),
  fieldPattern('must be a number conforming to the specified constraints', (label) =>
    fieldMust(label, 'doit être un nombre valide'),
  ),
  fieldPattern('must be an integer number', (label) =>
    fieldMust(label, 'doit être un nombre entier'),
  ),
  fieldPattern('must be a positive number', (label) =>
    fieldMust(label, 'doit être un nombre positif'),
  ),
  fieldPattern('must be a number string', (label) => fieldMust(label, 'doit être un nombre')),
  fieldPattern('must be a number', (label) => fieldMust(label, 'doit être un nombre')),
  fieldPattern('must be a boolean value', (label) => fieldMust(label, 'doit être oui ou non')),
  fieldPattern('must be a boolean', (label) => fieldMust(label, 'doit être oui ou non')),
  fieldPattern('must be an array', (label) => fieldMust(label, 'est incomplet')),
  fieldPattern('should not be empty', (label) => fieldMust(label, 'est obligatoire')),
  fieldPattern('should not be null or undefined', (label) => fieldMust(label, 'est obligatoire')),
  fieldPattern('must be a valid enum value', (label) =>
    fieldMust(label, "n'est pas une valeur acceptée"),
  ),
  fieldPattern('must be one of the following values: (.+)', (label) =>
    fieldMust(label, "n'est pas une valeur acceptée"),
  ),
  fieldPattern('must be a UUID', (label) => fieldMust(label, "n'est pas valide")),
  fieldPattern('must be a valid UUID', (label) => fieldMust(label, "n'est pas valide")),
  fieldPattern('must be a valid ISO 8601 date string', (label) =>
    fieldMust(label, 'doit être une date valide'),
  ),
  fieldPattern('must be a date string', (label) => fieldMust(label, 'doit être une date valide')),
  fieldPattern('must be a Date instance', (label) => fieldMust(label, 'doit être une date valide')),
  fieldPattern('must be an email', (label) =>
    fieldMust(label, 'doit être une adresse e-mail valide'),
  ),
  fieldPattern('must be a phone number', (label) =>
    fieldMust(label, 'doit être un numéro de téléphone valide'),
  ),
  fieldPattern('must be a URL address', (label) =>
    fieldMust(label, 'doit être une adresse web valide'),
  ),
  fieldPattern(String.raw`must be longer than or equal to (\d+) characters`, (label, match) =>
    fieldMust(label, `doit contenir au moins ${match[2]} caractères`),
  ),
  fieldPattern(String.raw`must be shorter than or equal to (\d+) characters`, (label, match) =>
    fieldMust(label, `doit contenir au maximum ${match[2]} caractères`),
  ),
  fieldPattern(String.raw`must not be less than (\d+)`, (label, match) =>
    fieldMust(label, `doit être au moins ${match[2]}`),
  ),
  fieldPattern(String.raw`must not be greater than (\d+)`, (label, match) =>
    fieldMust(label, `doit être au plus ${match[2]}`),
  ),
  fieldPattern(String.raw`must contain at least (\d+) elements`, (label) =>
    fieldMust(label, 'est incomplet'),
  ),
  fieldPattern(String.raw`must contain no more than (\d+) elements`, (label) =>
    fieldMust(label, 'contient trop d’éléments'),
  ),
  fieldPattern('must match (.+) regular expression', (label) =>
    fieldMust(label, 'n’a pas le bon format'),
  ),
  {
    pattern: /^nested property ([\w.]+) must be either object or array$/,
    translate: BackendErrors['nested property $1 must be either object or array'],
  },
  {
    pattern: /^property ([\w.]+) should not exist$/,
    translate: BackendErrors['property $1 should not exist'],
  },
];

/** Backend catalog keys handled by {@link SPECIAL_PATTERNS} instead of $1 regexes. */
const SKIP_CATALOG_TEMPLATES = new Set([
  'Named account $1 not found',
  'You do not have access to this $1',
]);

/** Turn a {@link BackendErrors} `$1` key into a regex that captures interpolated parts. */
function catalogKeyToPattern(key: string): RegExp {
  const escaped = key.replace(/[.*+?^${}()|[\]\\]/g, String.raw`\$&`);
  const withCaptures = escaped.replace(/\\\$(\d+)/g, '(.+)');
  return new RegExp(`^${withCaptures}$`);
}

/** {@link BackendErrors} keys with `$n` turned into match-and-replace patterns. */
const CATALOG_INTERPOLATED_PATTERNS: readonly Pattern[] = Object.keys(BackendErrors)
  .filter((key) => /\$\d+/.test(key) && !SKIP_CATALOG_TEMPLATES.has(key))
  .sort((a, b) => b.length - a.length)
  .map((key) => ({ pattern: catalogKeyToPattern(key), translate: BackendErrors[key] }));

/** Nest messages that need a custom translator (role labels, interface names). */
const SPECIAL_PATTERNS: readonly Pattern[] = [
  {
    pattern: /^(Student|Tutor|Parent|Coordinator|Manager|Volunteer|Recruiter) (.+) not found$/,
    translate: (m) => BackendErrors['Named account $1 not found'].replace('$1', getRoleLabel(m[1])),
  },
  {
    pattern: /^You do not have access to this (.+)$/,
    translate: (m) =>
      BackendErrors['You do not have access to this $1'].replace('$1', mapInterfaceName(m[1])),
  },
  {
    pattern:
      /^Credential "(.+)" is not listed in TEST_USERS\. Add it to the env whitelist first\.$/,
    translate: GENERIC,
  },
  {
    pattern:
      /^Role "(.+)" not found for company (.+)\. Run the roles seeder(?: and migration)? first\.$/,
    translate: GENERIC,
  },
  {
    pattern: /^student_remote role not found for company (.+)$/,
    translate: GENERIC,
  },
];

/** All Nest/validation regexes tried when mapping a backend message. */
const PATTERNS: readonly Pattern[] = [
  ...VALIDATION_PATTERNS,
  ...SPECIAL_PATTERNS,
  ...CATALOG_INTERPOLATED_PATTERNS,
];

/** French noun phrase for a Nest “this $1” interface name. */
function mapInterfaceName(name: string): string {
  const interfaceNames: Record<string, string> = {
    user: 'cet utilisateur',
    users: 'ces utilisateurs',
    session: 'cette séance',
    sessions: 'ces séances',
    establishment: 'cet établissement',
    establishments: 'ces établissements',
    role: 'ce rôle',
    roles: 'ces rôles',
    program: 'ce programme',
    level: 'ce niveau',
    subject: 'cette matière',
    'school year': 'cette année scolaire',
    holiday: 'ces vacances',
    partner: 'ce partenaire',
    'sport club': 'ce club sportif',
    admin: 'cet administrateur',
    volunteer: 'ce volontaire',
    activity: 'cette activité',
    'activity type': "ce type d'activité",
    'compte rendu': 'ce compte rendu',
    student: 'cet élève',
    postit: 'ce post-it',
    postits: 'ces post-its',
    'post-it': 'ce post-it',
    'post-its': 'ces post-its',
    report: 'ce compte rendu',
    reports: 'ces comptes rendus',
    notification: 'cette notification',
  };
  return interfaceNames[name.toLowerCase()] ?? name;
}

/** Map one Nest English (or FR legacy) message through {@link BackendErrors} / patterns. */
function mapSingle(message: string): string {
  const trimmed = message.trim();
  if (!trimmed) return trimmed;

  const mapped = BackendErrors[trimmed];
  if (mapped) return mapped;

  for (const { pattern, translate } of PATTERNS) {
    const match = pattern.exec(trimmed);
    if (!match) continue;
    if (typeof translate === 'function') return translate(match);
    return translate.replace(/\$(\d+)/g, (_, index: string) => match[Number(index)] ?? '');
  }

  return trimmed;
}

/** Map a Nest English (or FR legacy) message through {@link BackendErrors}. */
export function mapBackendMessage(message: string | string[]): string | string[] {
  if (Array.isArray(message)) {
    return message.map((entry) => (typeof entry === 'string' ? mapSingle(entry) : entry));
  }
  return mapSingle(message);
}

// ── Firebase / Nest helpers ──────────────────────────────────────────────────

export function pickNestMessage(data: unknown): string | undefined {
  if (!data || typeof data !== 'object') return undefined;
  const raw = (data as { message?: unknown }).message;
  if (typeof raw === 'string' && raw.trim()) return mapBackendMessage(raw.trim()) as string;
  if (Array.isArray(raw) && typeof raw[0] === 'string' && raw[0].trim()) {
    const mapped = mapBackendMessage(raw);
    return Array.isArray(mapped) ? mapped[0] : mapped;
  }
  return undefined;
}

/** Nest phrase that denies tutor-app access (matched as a substring). */
const TUTOR_APP_ACCESS_DENIED_PHRASE = "Vous n'êtes pas autorisé à accéder à cette application";
/** Nest phrase that says the account is not activated yet. */
const PENDING_ACTIVATION_PHRASE = "Votre compte n'est pas encore activé";

/** True when Nest copy says the account is not activated yet. */
export function isPendingActivationMessage(text: string): boolean {
  return text.includes(PENDING_ACTIVATION_PHRASE);
}

/** True for a 403 whose Nest message is the pending-activation phrase. */
export function isPendingActivationError(error: unknown): boolean {
  if (typeof error === 'object' && error !== null) {
    const err = error as { response?: { status?: number; data?: unknown } };
    if (err.response?.status !== 403) return false;
    const message = pickNestMessage(err.response?.data) ?? '';
    return isPendingActivationMessage(message);
  }
  return false;
}

/** Catalog copy for a pending-activation account. */
export const getPendingActivationMessage = (): string => CatalogErrors.accountPendingActivation;

/** Read then clear the pending-activation flag from sessionStorage. */
export const consumePendingActivationSessionMessage = (): string | null => {
  if (typeof sessionStorage === 'undefined') return null;
  if (sessionStorage.getItem('pending_activation') !== 'true') return null;
  const apiMessage = sessionStorage.getItem('pending_activation_message');
  sessionStorage.removeItem('pending_activation');
  sessionStorage.removeItem('pending_activation_message');
  const trimmed = apiMessage?.trim();
  return trimmed && trimmed.length > 0 ? trimmed : getPendingActivationMessage();
};

/** True when Nest copy denies tutor-app access. */
export function isTutorPlatformAccessDeniedMessage(text: string): boolean {
  return text.includes(TUTOR_APP_ACCESS_DENIED_PHRASE);
}

/** True when the user has the in-person `tutor` role code. */
export function hasTutorRole(roles: { code: string }[]): boolean {
  return roles.some((r) => r.code === 'tutor');
}

/** True for Firebase/network/timeout failures that should show the network catalog. */
function isNetworkError(error: unknown): boolean {
  if (typeof error === 'object' && error !== null) {
    const err = error as { code?: string; message?: string };
    if (err.code === 'auth/network-request-failed' || err.code === 'auth/timeout') return true;
    if (
      err.message &&
      (err.message.includes('network') ||
        err.message.includes('offline') ||
        err.message.includes('timeout') ||
        err.message.includes('Failed to fetch'))
    ) {
      return true;
    }
  }
  return false;
}

/** True for a 403 whose Nest message denies tutor-app access. */
export const isTutorBlockedError = (error: unknown): boolean => {
  if (typeof error === 'object' && error !== null) {
    const err = error as { response?: { status?: number; data?: unknown } };
    if (err.response?.status !== 403) return false;
    const message = pickNestMessage(err.response?.data) ?? '';
    return isTutorPlatformAccessDeniedMessage(message);
  }
  return false;
};

/** Catalog copy when a tutor is blocked from the web app. */
export const getTutorBlockedMessage = (): string => CatalogErrors.tutorAppAccessDenied;

/** Read then clear the tutor-blocked flag from sessionStorage. */
export const consumeTutorBlockedSessionMessage = (): string | null => {
  if (typeof sessionStorage === 'undefined') return null;
  if (sessionStorage.getItem('tutor_blocked') !== 'true') return null;
  const apiMessage = sessionStorage.getItem('tutor_blocked_message');
  sessionStorage.removeItem('tutor_blocked');
  sessionStorage.removeItem('tutor_blocked_message');
  const trimmed = apiMessage?.trim();
  return trimmed && trimmed.length > 0 ? trimmed : getTutorBlockedMessage();
};

/** Firebase Auth `auth/*` codes mapped to catalog error codes. */
const FIREBASE_ERROR_CODES: Record<string, CatalogErrorCode> = {
  'auth/user-not-found': 'firebaseUserNotFound',
  'auth/wrong-password': 'firebaseWrongPassword',
  'auth/invalid-email': 'invalidEmail',
  'auth/user-disabled': 'accountPendingActivation',
  'auth/email-already-in-use': 'firebaseEmailInUse',
  'auth/invalid-phone-number': 'invalidPhone',
  'auth/invalid-verification-code': 'firebaseInvalidOtp',
  'auth/invalid-verification-id': 'firebaseInvalidVerificationSession',
  'auth/code-expired': 'firebaseOtpExpired',
  'auth/missing-phone-number': 'firebasePhoneRequired',
  'auth/quota-exceeded': 'firebaseSmsQuotaExceeded',
  'auth/too-many-requests': 'firebaseTooManyRequests',
  'auth/operation-not-allowed': 'firebaseOperationNotAllowed',
  'auth/network-request-failed': 'network',
  'auth/timeout': 'firebaseTimeout',
  'auth/expired-action-code': 'firebaseResetLinkExpired',
  'auth/invalid-action-code': 'firebaseResetLinkInvalid',
  'auth/weak-password': 'passwordMinLength',
  'auth/invalid-credential': 'firebaseWrongPassword',
  'auth/credential-already-in-use': 'firebaseCredentialInUse',
  'auth/invalid-user-token': 'sessionExpired',
  'auth/user-token-expired': 'sessionExpired',
  'auth/captcha-check-failed': 'firebaseCaptchaFailed',
  'auth/invalid-app-credential': 'firebaseInvalidConfig',
  'auth/internal-error': 'firebaseInternal',
  'auth/invalid-api-key': 'firebaseInvalidConfig',
  'auth/app-deleted': 'firebaseAppDeleted',
  'auth/app-not-authorized': 'firebaseAppNotAuthorized',
  'auth/argument-error': 'firebaseInvalidArguments',
  'auth/invalid-persistence-type': 'firebaseInvalidPersistence',
  'auth/unsupported-persistence-type': 'firebaseUnsupportedPersistence',
  'auth/keychain-error': 'firebaseKeychain',
  'auth/missing-iframe-start': 'firebaseInit',
  'auth/auth-domain-config-required': 'firebaseAuthDomainRequired',
  'auth/cancelled-popup-request': 'firebasePopupCancelled',
  'auth/popup-blocked': 'firebasePopupBlocked',
  'auth/popup-closed-by-user': 'firebasePopupClosed',
  'auth/unauthorized-domain': 'firebaseUnauthorizedDomain',
  'auth/web-storage-unsupported': 'firebaseWebStorageUnsupported',
};

/** Catalog copy for a Firebase Auth error code. */
function getFirebaseErrorMessage(errorCode: string): string {
  return CatalogErrors[FIREBASE_ERROR_CODES[errorCode] ?? 'generic'];
}

/** Read a Firebase-style `code` from an error object, or `unknown`. */
function getFirebaseErrorCode(error: unknown): string {
  if (error && typeof error === 'object' && 'code' in error) {
    return (error as { code: string }).code;
  }
  return 'unknown';
}

// ── Distributor ──────────────────────────────────────────────────────────────

function readStatus(error: unknown): number | undefined {
  if (!error || typeof error !== 'object') return undefined;
  const status = (error as { response?: { status?: number } }).response?.status;
  return typeof status === 'number' ? status : undefined;
}

/** Axios/Nest `response.data` when present. */
function readResponseData(error: unknown): unknown {
  if (!error || typeof error !== 'object') return undefined;
  return (error as { response?: { data?: unknown } }).response?.data;
}

/** True when text already looks like French user-facing copy. */
function isLikelyUserCopy(text: string): boolean {
  return (
    /[àâäéèêëïîôùûüçœ]/i.test(text) ||
    /^(Vous |Veuillez |Impossible |Cette |Ce |Cet |Le |La |Les |Un |Une |Aucun |Aucune )/i.test(
      text,
    )
  );
}

/** Map raw Nest/API text through {@link BackendErrors}, or fall back to generic. */
function mapText(raw: string): string {
  const trimmed = raw.trim();
  const mapped = mapBackendMessage(trimmed);
  if (typeof mapped === 'string' && mapped !== trimmed) return mapped;
  if (typeof mapped === 'string' && isLikelyUserCopy(mapped)) return mapped;
  return CatalogErrors.generic;
}

/** True for an Axios error or any object that carries an HTTP status. */
function isApiError(error: unknown): boolean {
  return axios.isAxiosError(error) || readStatus(error) != null;
}

/** True when the request never got an HTTP response (network / timeout). */
function isTransportFailure(error: unknown): boolean {
  if (axios.isAxiosError(error) && !error.response) return true;
  return isNetworkError(error);
}

/** True for Axios default “Request failed with status code N” messages. */
function isOpaqueAxiosMessage(text: string): boolean {
  return /^Request failed with status code \d+$/.test(text) || /^Error \d{3}$/.test(text);
}

/** User-facing copy from an HTTP/Axios error (Nest body, status, or network). */
function messageFromApiError(error: unknown): string {
  const status = readStatus(error);
  if (status === 401) return CatalogErrors.sessionExpired;

  const api = pickNestMessage(readResponseData(error));
  if (api) {
    const mapped = mapText(api);
    if (mapped !== CatalogErrors.generic && !isOpaqueAxiosMessage(api)) {
      return mapped;
    }
  }

  if (status != null && HttpStatusErrors[status]) {
    return CatalogErrors[HttpStatusErrors[status]];
  }

  if (isTransportFailure(error)) return CatalogErrors.network;
  return CatalogErrors.generic;
}

/** Read a `message` string from an Error or a plain error object. */
function readOwnMessage(error: object): string | undefined {
  if (error instanceof Error && error.message.trim()) return error.message;
  if (!('message' in error)) return undefined;
  const message = (error as { message?: unknown }).message;
  if (typeof message === 'string' && message.trim()) return message;
  if (Array.isArray(message) && typeof message[0] === 'string') return message[0];
  return undefined;
}

/** User-facing copy from a Firebase or plain error object. */
function messageFromObject(error: object): string {
  const firebaseCode = getFirebaseErrorCode(error);
  if (firebaseCode.startsWith('auth/')) return getFirebaseErrorMessage(firebaseCode);
  const raw = readOwnMessage(error);
  if (raw && isCatalogErrorCode(raw)) return CatalogErrors[raw];
  return raw ? mapText(raw) : CatalogErrors.generic;
}

/** User-facing message from a catalog code, Nest/Firebase error, or fallback. */
export function getErrorMessage(error: unknown): string {
  if (error == null || error === '') return CatalogErrors.generic;
  if (isCatalogErrorCode(error)) return CatalogErrors[error];
  if (isInterpolatedInternalError(error)) return resolveInterpolatedInternalError(error);
  if (typeof error === 'string') return mapText(error);
  if (isApiError(error)) return messageFromApiError(error);
  if (isNetworkError(error)) return CatalogErrors.network;
  if (typeof error === 'object') return messageFromObject(error);
  return CatalogErrors.generic;
}
